import "./CMSSavingsTracker.scss";

import { ControlledInput } from "components/Card/ControlledInput";
import { MultiColumnCard } from "components/Card/MultiColumnCard";
import { KaboodleToaster } from "components/common";
import { MaskedInput } from "components/inputs";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import { omit, throttle } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import {
  prescriptionAllocationAPI,
  treatmentAllocationAPI,
  useGetAllPrescriptionAllocationsQuery,
  useGetAllTreatmentAllocationsQuery,
  usePatchPrescriptionAllocationByIdMutation,
  usePatchTreatmentAllocationByIdMutation,
} from "services/msa-api";
import type {
  CostProjectionDataType,
  MSADataType,
  PrescriptionRow,
  TreatmentRow,
} from "types/DataTypes";
import { useToken } from "utils/context";
import { formatCurrency } from "utils/format-utils";

import { getServiceField } from "../future-medical-utils";

import type { AppDispatch } from "services/store";

type AllocationRow = TreatmentRow | PrescriptionRow;
type RowWithType = AllocationRow & { rowType: string };

const isPrescriptionRow = (row: AllocationRow): row is PrescriptionRow => {
  return "ndc" in row;
};

type SummarySectionProps = {
  totals: {
    savingsTotal: number;
    mitigationsTotal: number;
    overallTotal: number;
  };
};

const SummarySection = ({ totals }: SummarySectionProps) => {
  const { savingsTotal = 0, mitigationsTotal = 0, overallTotal = 0 } = totals;

  return (
    <div className="data-block">
      <div className="data-block-header">Savings + Mitigations Summary</div>
      <div className="data-values">
        <label>
          <b>Savings Total</b>
        </label>
        <figure className="w-dollar">{formatCurrency(savingsTotal)}</figure>

        <label>
          <b>Mitigations Total</b>
        </label>
        <figure className="w-dollar">{formatCurrency(mitigationsTotal)}</figure>

        <div className="border grid-2-wide" />

        <label>
          <b>Overall Total</b>
        </label>
        <figure className="w-dollar">
          <b>{formatCurrency(overallTotal)}</b>
        </figure>
      </div>
    </div>
  );
};

type CMSSavingsRowProps = RowWithType & {
  onRowChange: (
    newSelectedRows: Set<string | number>,
    currentRow: AllocationRow,
    title: string,
  ) => void;
  onSavingsUpdate: (
    row: AllocationRow,
    savingsAmount: number,
    type: "exposure" | "mitigation",
  ) => Promise<void>;
  title: "Exposures" | "Mitigations";
};

const CMSSavingsRow = (props: CMSSavingsRowProps) => {
  const { onRowChange, onSavingsUpdate, title } = props;
  const isExposures = title === "Exposures";
  const type = isExposures ? "exposure" : "mitigation";

  const isRealized = isExposures
    ? props.exposure_savings_realized
    : props.mitigation_savings_realized;

  const projectedValue = isExposures
    ? (props.total_exposure ?? 0)
    : (props.total_mitigation ?? 0);

  const {
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: props,
  });

  useEffect(() => {
    reset(props, {
      keepDirtyValues: true,
    });
  }, [reset, props, type, isRealized]);

  const handleFieldSave = useCallback(
    throttle(
      async (fieldName: string, value: string | number) => {
        try {
          const numericValue =
            typeof value === "string" ? parseFloat(value) || 0 : value;
          await onSavingsUpdate(props, numericValue, type);
        } catch (error) {
          console.error("Failed to update savings amount:", error);
          KaboodleToaster.error("Failed to update savings amount");
        }
      },
      800,
      { leading: true, trailing: true },
    ),
    [props.id, onSavingsUpdate, type, isRealized],
  );

  const handleRealizedChange = async () => {
    const rowId = `${props.rowType}-${props.id}`;
    onRowChange(new Set([rowId]), props, title);
  };

  return (
    <>
      {/* Empty drag handle space */}
      <figure></figure>

      {/* Realized Checkbox */}
      <Checkbox
        name={`realized-${props.id}`}
        label=""
        checked={isRealized}
        required={false}
        onChange={handleRealizedChange}
      />

      {/* Name */}
      <figure>{props.name}</figure>

      {/* Savings Input */}
      <figure>
        {isRealized ? (
          <ControlledInput
            control={control}
            name={
              isExposures
                ? "total_exposure_savings"
                : "total_mitigation_savings"
            }
            errors={errors}
            onSave={(value) =>
              handleFieldSave(
                isExposures
                  ? "total_exposure_savings"
                  : "total_mitigation_savings",
                value,
              )
            }
            componentType={MaskedInput}
            mask="dollar"
          />
        ) : (
          <ControlledInput
            control={control}
            name="total_exposure_savings"
            errors={errors}
            onSave={(value) => handleFieldSave("total_exposure_savings", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly={true}
          />
        )}
      </figure>

      {/* Projected Amount */}
      <figure>{formatCurrency(projectedValue)}</figure>
    </>
  );
};

type SavingsTableProps = {
  rows: RowWithType[];
  title: "Exposures" | "Mitigations";
  onRowChange: (
    newSelectedRows: Set<string | number>,
    currentRow: AllocationRow,
    title: string,
  ) => void;
  onSavingsUpdate: (
    row: AllocationRow,
    savingsAmount: number,
    type: "exposure" | "mitigation",
  ) => Promise<void>;
};

const SavingsTable = ({
  rows,
  title,
  onRowChange,
  onSavingsUpdate,
}: SavingsTableProps) => {
  const isExposures = title === "Exposures";

  const RowComponent = useCallback(
    (row: RowWithType) => (
      <CMSSavingsRow
        {...row}
        onRowChange={onRowChange}
        onSavingsUpdate={onSavingsUpdate}
        title={title}
      />
    ),
    // crucial to keep re-rendering to minimum for keepDirtyValues to work
    [title],
  );

  return (
    <MultiColumnCard
      classNames="cms-savings-allocation"
      heading={title}
      columnNames={[
        "",
        "Realized",
        "Name",
        isExposures ? "Total Savings ($)" : "Total Mitigated ($)",
        "Projected",
      ]}
      initialRows={rows}
      RowComponent={RowComponent}
      getRowKey={(row) => `${row.rowType}-${row.id}`}
      isLoading={false}
      showAddButton={false}
      onNewRow={() => {
        // Not applicable for this component
      }}
    />
  );
};

type CMSSavingsTrackerProps = {
  service: MSADataType | CostProjectionDataType;
};

/*
 * this component is used to display exposure and mitigation savings for the CMS submission.
 * it is used in the FMCMSSubmission component.
 */
const CMSSavingsTracker = ({ service }: CMSSavingsTrackerProps) => {
  const { accessToken } = useToken();
  const { msaId, costProjectionId } = useParams<{
    msaId?: string;
    costProjectionId?: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const [patchTreatmentAllocation] = usePatchTreatmentAllocationByIdMutation();
  const [patchPrescriptionAllocation] =
    usePatchPrescriptionAllocationByIdMutation();

  const serviceId = msaId || costProjectionId;

  const { data: treatmentData, isLoading: isLoadingTreatments } =
    useGetAllTreatmentAllocationsQuery(
      {
        params: getServiceField(service, true),
      },
      { skip: !accessToken || !serviceId },
    );

  const { data: prescriptionData, isLoading: isLoadingPrescriptions } =
    useGetAllPrescriptionAllocationsQuery(
      {
        params: getServiceField(service, true),
      },
      { skip: !accessToken || !serviceId },
    );

  const allTreatments =
    (treatmentData as unknown as { results: TreatmentRow[] })?.results || [];
  const allPrescriptions =
    (prescriptionData as unknown as { results: PrescriptionRow[] })?.results ||
    [];

  useEffect(() => {
    dispatch(setIsLoading(isLoadingTreatments || isLoadingPrescriptions));
  }, [dispatch, isLoadingTreatments, isLoadingPrescriptions]);

  const { sortedExposures, sortedMitigations } = useMemo(() => {
    const mapWithType = (rows: AllocationRow[], type: string) =>
      rows.map((row) => ({ ...row, rowType: type }));

    const allRows = [
      ...mapWithType(allTreatments, "treatment"),
      ...mapWithType(allPrescriptions, "prescription"),
    ];

    const includedRows = allRows.filter(
      (row) => row.has_exposure || row.has_mitigation,
    );

    return {
      sortedExposures: includedRows
        .filter((row) => row.has_exposure)
        .sort((a, b) => a.name.localeCompare(b.name)),
      sortedMitigations: includedRows
        .filter((row) => row.has_mitigation)
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [allTreatments, allPrescriptions]);

  const totals = useMemo(() => {
    // Calculate totals from realized savings on individual rows
    const allRows = [...sortedExposures, ...sortedMitigations];

    const savingsTotal = allRows.reduce((sum, row) => {
      const savingsAmount = Number(row.total_exposure_savings) || 0;
      return sum + (row.exposure_savings_realized ? savingsAmount : 0);
    }, 0);

    const mitigationsTotal = allRows.reduce((sum, row) => {
      const mitigationAmount = Number(row.total_mitigation_savings) || 0;
      return sum + (row.mitigation_savings_realized ? mitigationAmount : 0);
    }, 0);

    return {
      savingsTotal,
      mitigationsTotal,
      overallTotal: savingsTotal + mitigationsTotal,
    };
  }, [sortedExposures, sortedMitigations]);

  if (!serviceId || !accessToken) return null;

  const handleSelectedRowsChange = async (
    _newSelectedRows: Set<string | number>,
    currentRow: AllocationRow,
    title: string,
  ) => {
    const newExposureValue =
      title === "Exposures"
        ? !currentRow.exposure_savings_realized
        : currentRow.exposure_savings_realized;
    const newMitigationValue =
      title === "Mitigations"
        ? !currentRow.mitigation_savings_realized
        : currentRow.mitigation_savings_realized;

    let patchResult: { undo: () => void };

    if (isPrescriptionRow(currentRow)) {
      patchResult = dispatch(
        prescriptionAllocationAPI.util.updateQueryData(
          "getAllPrescriptionAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const row = (
              draft as unknown as { results: PrescriptionRow[] }
            ).results.find((r: PrescriptionRow) => r.id === currentRow.id);
            if (!row) return;
            if (row.has_exposure) {
              row.exposure_savings_realized = newExposureValue;
            }
            if (row.has_mitigation) {
              row.mitigation_savings_realized = newMitigationValue;
            }
          },
        ),
      );
    } else {
      patchResult = dispatch(
        treatmentAllocationAPI.util.updateQueryData(
          "getAllTreatmentAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const row = (
              draft as unknown as { results: TreatmentRow[] }
            ).results.find((r: TreatmentRow) => r.id === currentRow.id);
            if (!row) return;
            if (row.has_exposure) {
              row.exposure_savings_realized = newExposureValue;
            }
            if (row.has_mitigation) {
              row.mitigation_savings_realized = newMitigationValue;
            }
          },
        ),
      );
    }

    try {
      const basePatchData = {
        exposure_savings_realized: newExposureValue,
        mitigation_savings_realized: newMitigationValue,
        name: currentRow.name,
        exposure_detail_type: currentRow.exposure_detail_type,
        number_of_years: currentRow.number_of_years,
        number_of_years_exposed: currentRow.number_of_years_exposed,
        number_of_years_mitigated: currentRow.number_of_years_mitigated,
        exposed_unit_price: currentRow.exposed_unit_price,
        mitigated_unit_price: currentRow.mitigated_unit_price,
        total_exposure_savings: currentRow.total_exposure_savings,
        total_mitigation_savings: currentRow.total_mitigation_savings,
        ...getServiceField(service, false),
      };

      const patchData = isPrescriptionRow(currentRow)
        ? {
            ...basePatchData,
            ndc: currentRow.ndc,
            dosage: currentRow.dosage,
            units_per_month: currentRow.units_per_month,
            units_per_year: currentRow.units_per_year,
            unit_price: currentRow.unit_price,
            exposed_units_per_month: currentRow.exposed_units_per_month,
            mitigated_units_per_month: currentRow.mitigated_units_per_month,
            exposed_units_per_year: currentRow.exposed_units_per_year,
            mitigated_units_per_year: currentRow.mitigated_units_per_year,
          }
        : {
            ...basePatchData,
            units_per_year: currentRow.units_per_year,
            exposed_units_per_year: currentRow.exposed_units_per_year,
            mitigated_units_per_year: currentRow.mitigated_units_per_year,
          };

      const fieldsToOmit = [
        "cost_projection",
        "total_cost",
        "annual_cost",
        "organization",
        "modified",
        "created",
        "version",
        "id",
        "msa",
      ];

      const finalPatchData = omit(patchData, fieldsToOmit);

      if (isPrescriptionRow(currentRow)) {
        await patchPrescriptionAllocation({
          id: currentRow.id,
          data: finalPatchData,
        }).unwrap();
      } else {
        await patchTreatmentAllocation({
          id: currentRow.id,
          data: finalPatchData,
        }).unwrap();
      }
    } catch (err) {
      patchResult.undo();
      KaboodleToaster.error("Failed to update realized status");
      console.error("Failed to update realized status:", err);
    }
  };

  const handleSavingsUpdate = async (
    row: AllocationRow,
    savingsAmount: number,
    type: "exposure" | "mitigation",
  ) => {
    let patchResult: { undo: () => void };
    const fieldName =
      type === "exposure"
        ? "total_exposure_savings"
        : "total_mitigation_savings";

    if (isPrescriptionRow(row)) {
      patchResult = dispatch(
        prescriptionAllocationAPI.util.updateQueryData(
          "getAllPrescriptionAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const foundRow = (
              draft as unknown as { results: PrescriptionRow[] }
            ).results.find((r: PrescriptionRow) => r.id === row.id);
            if (foundRow) {
              foundRow[fieldName] = savingsAmount;
            }
          },
        ),
      );
    } else {
      patchResult = dispatch(
        treatmentAllocationAPI.util.updateQueryData(
          "getAllTreatmentAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const foundRow = (
              draft as unknown as { results: TreatmentRow[] }
            ).results.find((r: TreatmentRow) => r.id === row.id);
            if (foundRow) {
              foundRow[fieldName] = savingsAmount;
            }
          },
        ),
      );
    }

    try {
      const basePatchData = {
        [fieldName]: savingsAmount,
        name: row.name,
        exposure_detail_type: row.exposure_detail_type,
        number_of_years: row.number_of_years,
        number_of_years_exposed: row.number_of_years_exposed,
        number_of_years_mitigated: row.number_of_years_mitigated,
        exposed_unit_price: row.exposed_unit_price,
        mitigated_unit_price: row.mitigated_unit_price,
        exposure_savings_realized: row.exposure_savings_realized,
        mitigation_savings_realized: row.mitigation_savings_realized,
        total_exposure_savings: row.total_exposure_savings,
        total_mitigation_savings: row.total_mitigation_savings,
        ...getServiceField(service, false),
      };

      // Update the field we're changing
      if (type === "exposure") {
        basePatchData.total_exposure_savings = savingsAmount;
      } else {
        basePatchData.total_mitigation_savings = savingsAmount;
      }

      const patchData = isPrescriptionRow(row)
        ? {
            ...basePatchData,
            ndc: row.ndc,
            dosage: row.dosage,
            units_per_month: row.units_per_month,
            units_per_year: row.units_per_year,
            unit_price: row.unit_price,
            exposed_units_per_month: row.exposed_units_per_month,
            mitigated_units_per_month: row.mitigated_units_per_month,
            exposed_units_per_year: row.exposed_units_per_year,
            mitigated_units_per_year: row.mitigated_units_per_year,
          }
        : {
            ...basePatchData,
            units_per_year: row.units_per_year,
            exposed_units_per_year: row.exposed_units_per_year,
            mitigated_units_per_year: row.mitigated_units_per_year,
          };

      const fieldsToOmit = [
        "cost_projection",
        "total_cost",
        "annual_cost",
        "organization",
        "modified",
        "created",
        "version",
        "id",
        "msa",
      ];

      const finalPatchData = omit(patchData, fieldsToOmit);

      if (isPrescriptionRow(row)) {
        await patchPrescriptionAllocation({
          id: row.id,
          data: finalPatchData,
        }).unwrap();
      } else {
        await patchTreatmentAllocation({
          id: row.id,
          data: finalPatchData,
        }).unwrap();
      }
    } catch (err) {
      patchResult.undo();
      KaboodleToaster.error("Failed to update savings amount");
      console.error("Failed to update savings amount:", err);
      throw err;
    }
  };

  return (
    <div className="cms-savings-tracker-container">
      <SavingsTable
        rows={sortedExposures}
        title="Exposures"
        onRowChange={(newSelectedRows, currentRow) =>
          handleSelectedRowsChange(newSelectedRows, currentRow, "Exposures")
        }
        onSavingsUpdate={handleSavingsUpdate}
      />
      <SavingsTable
        rows={sortedMitigations}
        title="Mitigations"
        onRowChange={(newSelectedRows, currentRow) =>
          handleSelectedRowsChange(newSelectedRows, currentRow, "Mitigations")
        }
        onSavingsUpdate={handleSavingsUpdate}
      />
      <SummarySection totals={totals} />
    </div>
  );
};

export default CMSSavingsTracker;
